import { ApolloError } from '@apollo/client';

/**
 * Determines if a membership activation error is a user validation error
 * (not a bug that should be reported to Honeybadger).
 *
 * These patterns match the backend's user_made_a_mistake validation errors
 * in services/membership_for_patient/contract_and_employee_code_hierarchy/
 * enterprise_membership_creator.rb
 *
 * @param errorMessage - The error message to check
 * @returns true if this is a known user validation error
 */
export const isValidationError = (errorMessage: string): boolean => {
  return (
    errorMessage.includes('Access Code is invalid') ||
    errorMessage.includes('not yet been granted eligibility') ||
    errorMessage.includes('DOB on file does not match')
  );
};

/**
 * Gets a user-friendly error message from an Apollo error, with proper fallbacks.
 * Ensures we always return a string, never false or undefined.
 *
 * @param error - The Apollo error object
 * @param defaultMessage - Fallback message if no GraphQL errors found
 * @returns A user-friendly error message string
 */
export const getMembershipActivationErrorMessage = (
  error: ApolloError,
  defaultMessage: string
): string => {
  // Try to get GraphQL error messages
  if (error.graphQLErrors?.length > 0) {
    const messages = error.graphQLErrors
      .map(err => err.message)
      .filter(Boolean)
      .join(', ');
    if (messages) {
      return messages;
    }
  }

  // Fallback to generic error message or default
  return error.message || defaultMessage;
};
